import { useMemo, useState } from "react";
import { CartHeader } from "components/cart/CartHeader";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import UpSellAndWishlistSection from "components/upsell/UpSellSection";
import CustomComponentSlot from "components/common/CustomComponentSlot";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useLocale } from "lib/hooks/useLocale";
import { UpsellConfigType } from "lib/types/merchantConfigTypes";
import { eventTypes } from "lib/utils/constants";
import { classNames, publishPostMessage, redirectUrl } from "lib/utils/helpers";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import ShopfloBadge from "components/common/ShopfloBadge";
import EmptyCartBagIllustration from "components/common/EmptyCartBagIllustration";
import { isDarkBackgroundColor } from "@shopflo/ui/utils";

export default function EmptyCart() {
  const { t } = useLocale();

  const checkoutContext = useCheckoutContext();
  const {
    actions: { setCheckoutModal },
  } = checkoutContext;

  const {
    state: {
      merchant,
      cartMetadata: { metadataLoading, uiAttributes, primaryCTAConfig },
    },
  } = useMerchantContext();
  const { emptyCartCtaText, emptyCartCtaUrl } = uiAttributes ?? {};

  const primaryBrandColor = merchant?.colorPallet?.primaryColor ?? "rgb(var(--flo-ink-color) / 1)";
  const primaryBrandTextColor = merchant?.colorPallet?.primaryTextColor ?? "rgb(var(--flo-paper-color) / 1)";
  const cartBackgroundColor = uiAttributes?.page?.backgroundColor ?? "rgb(var(--flo-paper-color) / 1)";
  const isDarkBackground = isDarkBackgroundColor(cartBackgroundColor);
  const primaryTextColor = isDarkBackground ? "rgb(255 255 255 / 1)" : "rgb(var(--flo-ink-color) / 1)";
  const secondaryTextColor = isDarkBackground
    ? "rgb(255 255 255 / 0.5)"
    : "rgb(var(--flo-ink-color) / 0.5)";
  const [isUpsellSectionVisible, setIsUpsellSectionVisible] = useState(false);

  const hasEmptyCartBodySlot = uiAttributes?.customComponentsConfig?.components?.some(
    (c: any) => c.position === "EMPTY_CART_BODY" && c.enabled,
  );

  const closeCart = () => {
    setCheckoutModal("NONE");
    publishPostMessage(eventTypes.CLOSE_CART_IFRAME, {});
  };

  const dynamicButton = (
    <div className="flex flex-col items-center justify-center">
      <button
        type="button"
        disabled={metadataLoading}
        onClick={() => {
          try {
            const url = new URL(emptyCartCtaUrl ?? "");
            if (url.protocol === "http:") {
              url.protocol = "https:";
            }
            redirectUrl(url.toString());
          } catch {
            closeCart();
          }
        }}
        className={classNames(
          "inline-flex items-center justify-center gap-2 px-4 py-2.5 text-text-md-semibold whitespace-nowrap rounded-xl",
          metadataLoading ? "cursor-not-allowed opacity-50" : "cursor-pointer",
        )}
        style={{
          backgroundColor: primaryCTAConfig?.backgroundColor ?? primaryBrandColor,
          color: primaryCTAConfig?.textColor ?? primaryBrandTextColor,
          borderRadius: primaryCTAConfig?.borderRadius,
        }}>
        {emptyCartCtaText?.length ? emptyCartCtaText : t("continue_shopping")}
      </button>
    </div>
  );

  const recentlyViewedSectionCustomisation: UpsellConfigType = useMemo(
    () => ({
      isEnabled: true,
      upsellTitle: "",
      discountedUpsellTitle: "",
      shouldOverrideDiscountedUpsell: false,
      backgroundColor: "transparent",
      borderColor: "transparent",
      textColor: isDarkBackground ? "rgb(255 255 255 / 0.8)" : "rgb(var(--flo-ink-color) / 0.8)",
      buttonColor: primaryCTAConfig?.backgroundColor ?? primaryBrandColor,
      buttonTextColor: primaryCTAConfig?.textColor ?? primaryBrandTextColor,
      offerBadgeColor: primaryBrandColor,
      titleColor: isDarkBackground ? "rgb(255 255 255 / 1)" : "rgb(var(--flo-ink-color) / 0.8)",
      cardRadius: 0,
      cardBorderWidth: 0,
      cardBorderSides: "all",
      clearUpsellItemsOnCartClose: false,
    }),
    [primaryBrandColor, primaryBrandTextColor, primaryCTAConfig, isDarkBackground],
  );

  const renderCartBody = () => {
    if (metadataLoading) {
      return <OverlaySpinner />;
    }

    return (
      <div className="flex flex-col items-center justify-center h-full w-full">
        <div className="mb-8">
          <EmptyCartBagIllustration brandLogoUrl={merchant?.logoUrl} />
        </div>
        <div className="flex flex-col items-center gap-1 pb-5 text-center">
          <div className="text-text-md-semibold" style={{ color: primaryTextColor }}>
            {t("cart_is_empty")}
          </div>
          <div className="text-text-sm-regular" style={{ color: secondaryTextColor }}>
            {t("cart_is_empty_subtitle")}
          </div>
        </div>
        {dynamicButton}
      </div>
    );
  };

  return (
    <div
      className="items-between relative flex h-full flex-col"
      style={{ backgroundColor: cartBackgroundColor, WebkitFontSmoothing: "antialiased" }}>
      <CartHeader customisations={uiAttributes?.header} showBottomShadow={true} />

      <div
        className="flex-1 flex flex-col overflow-y-auto"
        style={{
          backgroundColor: cartBackgroundColor,
        }}>
        <div className={classNames("flex flex-col flex-1 mx-3 mt-4")}>
          {hasEmptyCartBodySlot ? <CustomComponentSlot position="EMPTY_CART_BODY" /> : renderCartBody()}
        </div>
        <CustomComponentSlot position="EMPTY_CART_AFTER_CTA" />
        {!hasEmptyCartBodySlot && (
          <div
            className={classNames(
              "w-full inline-flex flex-col items-start",
              isUpsellSectionVisible ? "gap-4 pt-5 pl-6 pb-[3.75rem] mb-[115px]" : "",
            )}
            style={
              isUpsellSectionVisible
                ? {
                    background: `linear-gradient(180deg, color-mix(in srgb, ${primaryBrandColor} ${
                      isDarkBackground ? 100 : 10
                    }%, transparent) 0%, transparent 100%)`,
                  }
                : undefined
            }>
            <UpSellAndWishlistSection
              parent="EMPTY_CART"
              onVisibilityChange={setIsUpsellSectionVisible}
              customisation={recentlyViewedSectionCustomisation}
            />
          </div>
        )}
        <div className="flex flex-col items-center justify-end">
          <div className="flex justify-center items-center py-3">
            <ShopfloBadge />
          </div>
        </div>
      </div>
    </div>
  );
}
